import React from "react";

/**
 * Shared UI primitives for the SMART site.
 *
 * The layout language is card-based and light: white surfaces, hairline borders,
 * brand/butter tints for fills, and the brand indigo as the single accent that
 * marks the active state. Every section is assembled from these pieces so the
 * home page and the Careers/Policies pages stay visually consistent.
 */

const BUTTON_VARIANTS = {
  solid:
    "bg-brand text-white hover:bg-brand/90 shadow-sm hover:shadow-md",
  tint: "bg-brand-100 text-brand hover:bg-brand-200",
  outline:
    "border border-brand-200 bg-white text-brand hover:border-brand hover:bg-brand-50",
  white: "bg-white text-brand hover:bg-brand-50 shadow-sm",
  ghost: "text-darkgrey hover:text-brand hover:bg-brand-50",
};

const BUTTON_SIZES = {
  sm: "px-4 py-2 text-sm",
  md: "px-6 py-3 text-sm",
  lg: "px-8 py-4 text-base",
};

export const Button = ({
  variant = "solid",
  size = "md",
  as: Tag = "button",
  className = "",
  children,
  ...props
}) => (
  <Tag
    className={`inline-flex items-center justify-center gap-2 rounded-xl font-semibold transition-all duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 ${BUTTON_VARIANTS[variant]} ${BUTTON_SIZES[size]} ${className}`}
    {...props}
  >
    {children}
  </Tag>
);

const TILE_TONES = {
  tint: "bg-brand-100 text-brand",
  butter: "bg-butter-100 text-accent-600",
  solid: "bg-brand text-white",
  white: "bg-white text-brand",
};

const TILE_SIZES = {
  sm: "w-10 h-10 rounded-lg",
  md: "w-12 h-12 rounded-xl",
  lg: "w-14 h-14 rounded-xl",
};

/** Rounded, tinted square holding a react-icons component. */
export const IconTile = ({
  icon: Icon,
  tone = "tint",
  size = "md",
  className = "",
}) => (
  <div
    className={`flex flex-shrink-0 items-center justify-center transition-colors duration-300 ${TILE_TONES[tone]} ${TILE_SIZES[size]} ${className}`}
  >
    <Icon className={size === "sm" ? "h-4 w-4" : "h-6 w-6"} />
  </div>
);

const CARD_TONES = {
  white: "border-brand-200/70 bg-white",
  accent: "border-brand bg-brand",
};

/**
 * Card surface with a hairline border.
 *
 * The fill is chosen via `tone` rather than by passing a `bg-*` class through
 * `className`: Tailwind resolves conflicting utilities by their order in the
 * generated stylesheet, not by the order they appear in the class string, so an
 * incoming `bg-brand` would not reliably beat a hardcoded `bg-white`.
 */
export const Card = ({ tone = "white", className = "", children, ...props }) => (
  <div
    className={`rounded-2xl border transition-all duration-300 ${CARD_TONES[tone]} ${className}`}
    {...props}
  >
    {children}
  </div>
);

/** Small pill label that sits above a section title. */
export const Eyebrow = ({ children, className = "" }) => (
  <span
    className={`inline-flex items-center gap-2 rounded-full bg-brand-100 px-4 py-1.5 text-xs font-bold uppercase tracking-wider text-brand ${className}`}
  >
    <span className="h-1.5 w-1.5 rounded-full bg-brand" />
    {children}
  </span>
);

/**
 * Section heading: eyebrow + title + subtitle, with an optional action rendered
 * on the right (the reference design's "All Category" / "See All" buttons).
 */
export const SectionHead = ({
  eyebrow,
  title,
  subtitle,
  action,
  align = "left",
  className = "",
}) => {
  const centered = align === "center";

  return (
    <div
      className={`mb-12 gap-6 ${
        centered
          ? "flex flex-col items-center text-center"
          : "flex flex-col items-start md:flex-row md:items-end md:justify-between"
      } ${className}`}
    >
      <div className={centered ? "max-w-2xl" : "max-w-2xl"}>
        {eyebrow && <Eyebrow className="mb-4">{eyebrow}</Eyebrow>}
        <h2 className="text-3xl font-bold leading-tight text-ink sm:text-4xl">
          {title}
        </h2>
        {subtitle && (
          <p className="mt-3 text-base leading-relaxed text-darkgrey">
            {subtitle}
          </p>
        )}
      </div>
      {action && <div className="flex-shrink-0">{action}</div>}
    </div>
  );
};

/** Page-width wrapper. Matches the reference design's generous gutters. */
export const Container = ({ className = "", children }) => (
  <div className={`mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8 ${className}`}>
    {children}
  </div>
);
